<template>
  <div class="pending-sales">
    <page-header title="Pending Sales">
      <template #actions>
        <align>
          <span v-if="lastEbaySync">{{ $filters.datetime(lastEbaySync) }}</span>

          <icon-button
            tooltip="Generate Pending Sales"
            icon="update"
            :loading="scraping"
            @click="generatePendingSales"
          />

          <icon-button
            tooltip="Calculate Card Stats"
            icon="calculate"
            :loading="calculating"
            @click="calculateCardStats"
          />

          <icon-button
            tooltip="Generate Daily Sales Recap"
            icon="post_add"
            :loading="generating"
            @click="dailySalesRecap"
          />

          <icon-button
            tooltip="Submit all pending sales"
            icon="cloud_upload"
            :loading="submitting"
            @click="confirmAllPendingSales"
          />
        </align>
      </template>
    </page-header>

    <align flex collapse-end>
      <shared-filters
        show-recent-searches
        placeholder="Search"
        :filters.sync="filters"
        :sales-platforms="platformOptions"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <align small-gap>
        <pill-dropdown
          v-model="employee"
          :options="employeeFilterOptions"
        />

        <pill-dropdown
          v-model="status"
          :options="statusOptions"
        />

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      is-filtering
      :filters="filters"
      :results="numResults"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="rowing"
        title="You are all caught up!"
      />

      <template>
        <section class="pending-sales-list">
          <pending-sales-item
            v-for="pendingSale in pendingSales"
            :id="pendingSale.itemId"
            :ref="`pending-sale-item-${pendingSale.itemId}`"
            :key="pendingSale.itemId"
            :sale="pendingSale"
            @remove="removePendingSaleFromList"
            @purgatory="sendToPurgatory"
          />
        </section>

        <div class="load-more-container">
          <btn
            v-if="hasMoreData"
            kind="secondary"
            loading-text="Loading"
            :loading="loading"
            @click="nextPage"
          >
            Load More
          </btn>
        </div>
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';
import clone from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import firebase from 'firebase/compat/app';

import { saleService, searchService } from '@/services';

import { DEFAULT_PENDING_FILTERS, PENDING_SORT_OPTIONS, SELLING_PLATFORM_OPTIONS } from '@cardladder/shared/constants';

import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import SharedFilters from '@/components/SharedFilters';
import FilterResults from '@/components/FilterResults';

import PendingSalesItem from './PendingSalesItem';

export default {
  components: {
    DirectionSortOptions,
    FilterResults,
    PendingSalesItem,
    SharedFilters,
  },

  data() {
    return {
      limit: 100,
      pendingSales: [],
      loaded: false,
      page: 1,
      hasMoreData: true,
      loading: false,
      submitting: false,
      scraping: false,
      calculating: false,
      generating: false,
      lastEbaySync: null,
      numResults: null,
      showFilterModal: false,
      sortOptions: PENDING_SORT_OPTIONS,
      defaultFilters: DEFAULT_PENDING_FILTERS,
      platformOptions: SELLING_PLATFORM_OPTIONS.filter(({ sales }) => sales === true),
      statusOptions: [
        { value: 'standard', label: 'Standard' },
        { value: 'priceJump', label: 'Price Jump' },
      ],
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  mounted() {
    this.$bus.$on('keyboard-shortcut-enter', this.handleKeyboardEnter);
    this.$bus.$on('pending-sale-updated', this.updatePendingSale);
  },

  beforeDestroy() {
    this.$bus.$off('keyboard-shortcut-enter', this.handleKeyboardEnter);
    this.$bus.$off('pending-sale-updated', this.updatePendingSale);
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      employeeOptions: (state) => state.employeeOptions,
      pendingSalesEditing: (state) => state.pendingSalesEditing,
    }),

    showEmptyState() {
      return !this.loading && this.loaded && this.pendingSales.length === 0;
    },

    employeeFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...this.employeeOptions,
      ];
    },

    query: {
      get() {
        return this.$store.state.pendingSalesQuery;
      },

      set(value) {
        this.$store.commit('SET_PENDING_QUERY', value);
      },
    },

    sort: {
      get() {
        return this.$store.state.pendingSalesSort;
      },

      set(value) {
        this.$store.commit('SET_PENDING_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.pendingSalesDirection;
      },

      set(value) {
        this.$store.commit('SET_PENDING_DIRECTION', value);
      },
    },

    employee: {
      get() {
        const activeEmployee = this.$store.state.pendingSalesFilters.employee;

        if (this.employeeOptions.find((employee) => employee.value === activeEmployee)) {
          return activeEmployee;
        }

        return 'all';
      },

      set(value) {
        this.filters = {
          ...this.filters,
          employee: value,
        };
      },
    },

    status: {
      get() {
        const activeStatus = this.statusOptions.find(({ value }) => value === this.$store.state.pendingSalesFilters.status);

        if (activeStatus) {
          return this.$store.state.pendingSalesFilters.status;
        }

        return this.statusOptions[0].value;
      },

      set(value) {
        this.filters = {
          ...this.filters,
          status: value,
        };
      },
    },

    filters: {
      get() {
        return this.$store.state.pendingSalesFilters;
      },

      set(value) {
        this.$store.commit('SET_PENDING_FILTERS', value);
      },
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;

      this.search(true);
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const data = await searchService.search({
          index: 'pendingsales',
          query: this.query,
          page: this.page,
          limit: this.limit,
          filters: this.filters,
          sort: this.sort,
          direction: this.direction,
        });

        const pendingSales = data.hits;

        this.numResults = data.totalHits;

        if (reset) {
          this.pendingSales = pendingSales;
        } else {
          this.pendingSales = [...this.pendingSales, ...pendingSales];
        }

        if (pendingSales.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        this.$toasted.error('Error loading the pending sales');
      }

      this.getLastScrape();
      this.loaded = true;
      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },

    sendToPurgatory(pendingSale) {
      const index = this.pendingSales.findIndex(({ itemId }) => itemId === pendingSale.itemId);

      if (index > -1) {
        this.pendingSales.splice(index, 1);
      }
    },

    removePendingSaleFromList(id) {
      this.pendingSales = this.pendingSales.filter(({ itemId }) => itemId !== id);
    },

    updatePendingSale(pendingSale) {
      const index = this.pendingSales.findIndex(({ itemId }) => itemId === pendingSale.itemId);

      if (index > -1) {
        this.$set(this.pendingSales, index, pendingSale);

        if (this.filters.employee !== 'all' && this.filters.employee !== pendingSale.employee) {
          this.removePendingSaleFromList(pendingSale.itemId);
        }
      }
    },

    async generatePendingSales() {
      if (window.confirm('Are you sure you want to generate pending sales?')) {
        this.scraping = true;

        try {
          await firebase.firestore().collection('scrapeRunners').add({ start: true });

          this.$toasted.success('Pending sales have been started generation process!');
        } catch (e) {
          // swallow
        }

        this.scraping = false;
      }
    },

    async calculateCardStats() {
      if (window.confirm('Are you sure you want to calculate all stats?')) {
        this.calculating = true;

        try {
          await firebase.functions().httpsCallable('httpSyncCardStats')();

          this.$toasted.success('Card stats have been calculated!');
        } catch (e) {
          this.$toasted.error('Failed to calculate card stats');
          Sentry.captureException(e);
        }

        this.calculating = false;
      }
    },

    async dailySalesRecap() {
      if (window.confirm('Are you sure you want to create the daily sales recap post?')) {
        this.generating = true;

        try {
          await firebase.functions().httpsCallable('httpDailySalesRecap')();

          this.$toasted.success('Daily sales recap has been posted!');
        } catch (e) {
          this.$toasted.error('Failed to generate sales recap');
          Sentry.captureException(e);
        }

        this.generating = false;
      }
    },

    async confirmAllPendingSales() {
      if (window.confirm('Are you sure you submit all these pending sales?')) {
        this.submitting = true;

        try {
          const promises = [];

          this.pendingSales.forEach((pendingSale) => {
            promises.push(saleService.confirmSale(pendingSale));
          });

          await Promise.all(promises);
          this.$toasted.success('All pending sales have been submitted!');
          this.pendingSales = [];
          this.reset();
        } catch (e) {
          this.$toasted.error('Failed to submit all pending sales');
          Sentry.captureException(e);
        }

        this.submitting = false;
      }
    },

    async getLastScrape() {
      const salesScrapeConfig = await firebase.firestore().collection('config').doc('salesScrape').get();

      this.lastEbaySync = salesScrapeConfig.data().ebayLastSync.toDate();
    },

    handleKeyboardEnter() {
      if (!this.pendingSales || !this.pendingSales.length || this.pendingSalesEditing) {
        return;
      }

      this.$refs[`pending-sale-item-${this.pendingSales[0].itemId}`][0].confirmPendingSale();
    },

    clearFilters() {
      this.filters = {
        ...clone(DEFAULT_PENDING_FILTERS),
        employee: this.filters.employee,
        status: this.filters.status,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.pending-sales {
  @include container;
  max-width: none;
  min-height: 100%;
}

.pending-sales-list {
  display: grid;
  grid-gap: $gp * 2;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media($xxlarge-up) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media($large-down) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.field-container {
  width: 100%;
  max-width: 300px;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.load-more-container {
  margin-top: $gp * 2;
  display: flex;
  justify-content: center;
}
</style>
